import { aspectRatio } from '@helpers/aspectRatio';
import { BannerThemeOption, brand } from '@helpers/brand';
import fonts from '@helpers/fonts';
import { baseGrid } from '@helpers/grid';
import { Device, from, until } from '@helpers/media';
import { contentStyles, h1 } from '@helpers/typography';
import CallButtonStyles from '@stories/Components/Buttons/CallButton/CallButton.styles';
import AccreditationsStyles from '@stories/Components/Misc/Accreditations/Accreditations.styles';
import SectionLinksStyles from '@stories/Components/Misc/SectionLinks/SectionLinks.styles';
import SectionWrapper from '@stories/Components/Misc/SectionWrapper/SectionWrapper';
import TrustpilotBlockStyles from '@stories/Components/Misc/TrustpilotBlock/TrustpilotBlock.styles';
import styled from 'styled-components';
const Container = styled(SectionWrapper) `
  --sectionSpacing: 48px;

  position: relative;
  overflow: hidden;

  &[data-theme=${BannerThemeOption.GreenSeafoam}] {
    --accentColor: ${brand.green.dark};
    --accentColorAlt: ${brand.green.tea};
    --accentContrastColor: ${brand.white};
    --bgColor: ${brand.green.seafoam};
    --fgColor: ${brand.black};
  }

  &[data-theme=${BannerThemeOption.GreenTea}] {
    --accentColor: ${brand.green.dark};
    --accentColorAlt: ${brand.green.tea};
    --accentContrastColor: ${brand.white};
    --bgColor: ${brand.green.tea};
    --fgColor: ${brand.black};
  }

  &[data-theme=${BannerThemeOption.GreenDark}] {
    --accentColor: ${brand.green.medium};
    --accentColorAlt: ${brand.green.seafoam};
    --accentContrastColor: ${brand.white};
    --bgColor: ${brand.green.medium};
    --fgColor: ${brand.white};
  }
`;
const Inner = styled.div `
  ${baseGrid};
`;
const BackgroundLogo = styled.span `
  position: absolute;
  top: 75%;
  left: 50%;
  transform: translate(-54%, -50%);
  min-width: 1600px;
  width: 307%;
  opacity: 0.3;

  [data-theme]:not([data-theme=${BannerThemeOption.DarkCoral}]) & {
    filter: brightness(3) contrast(1);
    opacity: 0.1;
  }

  @media ${from(Device.TabletLarge)} {
    top: 50%;
    min-width: 1920px;
    width: 130%;

    [data-variant='image'] & {
      min-width: 960px;
      max-width: 1556px;
      top: 80%;
      left: 25%;
    }
  }

  @media ${from(Device.DesktopLarge)} {
    min-width: 2300px;
  }
`;
const ContentColumn = styled.div `
  position: relative;
  z-index: 1;
  grid-column: 1 / span 12;
  max-width: 696px;

  ${TrustpilotBlockStyles.Container} {
    margin-top: 24px;
  }

  @media ${until(Device.TabletLarge)} {
    order: 2;
  }

  @media ${from(Device.TabletLarge)} {
    grid-column: 1 / span 6;
    padding-right: 32px;
  }
`;
const ImageColumn = styled.div `
  @media ${from(Device.TabletLarge)} {
    position: absolute;
    top: 0;
    right: 0;
    width: 50%;
    height: 100%;
    overflow: hidden;
  }

  @media ${until(Device.TabletLarge)} {
    position: relative;
    padding-bottom: ${aspectRatio(1024, 588)}%;
    margin: -48px -24px 24px;
    grid-column: 1 / span 12;
  }
`;
const LinksColumn = styled.div `
  position: relative;
  z-index: 1;
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(12, 1fr);
  grid-column: 1 / span 12;

  @media ${until(Device.TabletLarge)} {
    margin-top: 32px;
    order: 2;
  }

  @media ${from(Device.TabletLarge)} {
    grid-column: 7 / span 6;
  }

  ${SectionLinksStyles.Container} {
    grid-column: 1 / span 12;
    grid-row: 1;
  }

  ${AccreditationsStyles.Container} {
    grid-column: 1 / span 6;
    grid-row: 2;
    width: 100%;

    @media ${until(Device.DesktopSmall)} {
      grid-column: 1 / span 12;
    }
  }

  ${AccreditationsStyles.Item} {
    width: 100%;
    flex: 1;
  }

  ${AccreditationsStyles.Link} {
    justify-content: center;
    width: 100%;
    padding: 16px 22px;
  }

  ${AccreditationsStyles.Logo} {
    max-height: 65px;
  }

  ${TrustpilotBlockStyles.Container} {
    justify-content: flex-start;
    flex-wrap: wrap;
    gap: 10px;
    background-color: ${brand.white};
    color: ${brand.black};
    padding: 13px 40px;
    border-radius: 6px;
    grid-column: 7 / span 6;
    grid-row: 2;

    @media ${until(Device.DesktopSmall)} {
      padding: 13px 24px;
      grid-column: 1 / span 12;
      grid-row: 3;
    }
  }

  ${TrustpilotBlockStyles.Logo}, ${TrustpilotBlockStyles.RatingImage} {
    max-height: 28px;
    object-fit: contain;
    margin-top: auto;
  }

  ${TrustpilotBlockStyles.Logo} {
    @media ${from(Device.Tablet)} {
      max-width: 40%;
    }
  }

  ${TrustpilotBlockStyles.RatingImage} {
    max-width: 53%;
    min-width: 80px;

    @media ${until(Device.Tablet)} {
      min-width: 148px;
    }
  }

  ${TrustpilotBlockStyles.RatingLabel} {
    margin-bottom: auto;
  }
`;
const ImageWrapper = styled.div `
  position: absolute;
  top: 0;
  right: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;
const Image = styled.img `
  position: absolute;
  top: 0;
  left: 0;
  display: block;
  background: ${brand.lightGrey};
  height: 100%;
  object-fit: cover;
  object-position: center;
  width: 100%;
`;
const RowContainer = styled.div `
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  row-gap: 16px;
  margin-top: 32px;

  @media ${from(Device.TabletLarge)} {
    flex-direction: row;
    align-items: stretch;
    row-gap: 28px;
    column-gap: 24px;
    margin-top: 24px;

    ${CallButtonStyles.Container} {
      height: 100%;
    }
  }
`;
const ContactWrapper = styled.div `
  ${CallButtonStyles.Telephone} {
    margin-bottom: 0;
  }
`;
const Title = styled.h1 `
  ${h1}
  margin-bottom: 0.5em;
`;
const TitleSupportingText = styled.p `
  color: currentColor;
  ${fonts.montserrat.regular};
  margin: 0 0 1em;
  font-size: 20px;
  line-height: 36px;

  @media ${from(Device.DesktopLarge)} {
    font-size: 28px;
    line-height: 36px;
    margin-top: -0.3em;
  }
`;
const Content = styled.div `
  ${contentStyles}
`;
const InnerHeroBannerStyles = {
    Container,
    BackgroundLogo,
    ContactWrapper,
    Content,
    ContentColumn,
    Image,
    ImageColumn,
    ImageWrapper,
    Inner,
    LinksColumn,
    RowContainer,
    Title,
    TitleSupportingText,
};
export default InnerHeroBannerStyles;
